import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Menu, X, LayoutDashboard, ShieldCheck, LogOut, ChevronDown, List } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import NotificationBell from "./NotificationBell";
import ChatAssistant from "./ChatAssistant";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [navSearch] = useSearchParams();
  // the site-menu shortcut exists ONLY in one exact situation: the desktop split-view
  // jobs workspace with a job open in the right-hand details pane (desktop layout,
  // split mode enabled in Control Tower, /jobs route, a selection in the URL)
  const [splitCapable, setSplitCapable] = useState(false);
  useEffect(() => {
    let live = true;
    const mq = window.matchMedia("(min-width: 1024px)");
    const check = () => {
      if (!mq.matches) { if (live) setSplitCapable(false); return; }
      api.get("/settings/public", { params: { _: Date.now() } })
        .then((r) => { if (live) setSplitCapable(r.data?.desktop_layout === "split"); })
        .catch(() => {});
    };
    check();
    mq.addEventListener("change", check);
    return () => { live = false; mq.removeEventListener("change", check); };
  }, []);
  const showMenuShortcut = splitCapable && location.pathname === "/jobs" && !!navSearch.get("selected");

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="nav-logo" className="font-display text-2xl font-black tracking-tight text-zinc-950">
            Hired<span className="text-blue-600">Today</span>
          </Link>

          <div className="hidden md:flex items-center gap-3">
            {user && <NotificationBell />}
            <ChatAssistant user={user} />
            {/* small, discreet shortcut: navigates to the homepage, then smooth-scrolls
                to the site menu in the footer (real route change — Back returns to /jobs) */}
            {showMenuShortcut && (
            <button type="button" data-testid="nav-menu-shortcut" aria-label="Go to homepage site menu" title="Site menu"
              onClick={() => {
                const go = () => document.querySelector('[data-testid="site-footer"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                if (window.location.pathname !== "/") {
                  navigate("/");
                  // scroll twice: once after the route settles, again after the homepage's
                  // content has finished loading (the footer moves as sections render)
                  setTimeout(go, 450);
                  setTimeout(go, 1400);
                } else { go(); }
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-zinc-200/80 bg-white/70 backdrop-blur-sm text-zinc-400 hover:text-blue-700 hover:border-blue-200 hover:bg-blue-50/60 transition-colors duration-200 shadow-sm">
              <List className="h-3.5 w-3.5" />
            </button>
            )}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger data-testid="account-menu-trigger"
                  className="flex items-center gap-2 text-sm font-medium text-zinc-800 border border-zinc-200 rounded-md px-4 py-2 hover:border-zinc-300 transition-colors duration-200 bg-white">
                  {user.name?.split(" ")[0] || "Account"} <ChevronDown className="h-4 w-4 text-zinc-400" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white border border-zinc-200 w-48">
                  {user.role === "admin" ? (
                    <DropdownMenuItem data-testid="menu-control-tower" onClick={() => navigate("/control-tower")}>
                      <ShieldCheck className="h-4 w-4 mr-2" /> Control Tower
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")}>
                      <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                  )}
                  {user.role !== "admin" && null}
                  <DropdownMenuItem data-testid="menu-logout" onClick={handleLogout}>
                    <LogOut className="h-4 w-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" data-testid="nav-login"
                className="text-sm font-medium text-zinc-700 hover:text-zinc-950 px-3 py-2 transition-colors duration-200">
                Log in
              </Link>
            )}
            {(!user || user.role !== "candidate") && (
              <Link to="/post-a-job" data-testid="nav-post-job"
                className="group inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-semibold px-5 py-2.5 rounded-md hover:bg-zinc-800 transition-colors duration-200">
                Post a job
              </Link>
            )}
          </div>

          <div className="flex items-center gap-1 md:hidden">
            {user && <NotificationBell />}
            <ChatAssistant user={user} toggleTestId="chat-assistant-toggle-mobile" />
            <button data-testid="mobile-menu-toggle" className="p-2 text-zinc-800" onClick={() => setOpen(!open)} aria-label="Menu">
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-zinc-100 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            <div className="space-y-2">
              {user ? (
                <>
                  <Link to={user.role === "admin" ? "/control-tower" : "/dashboard"} onClick={() => setOpen(false)}
                    data-testid="mobile-nav-dashboard"
                    className="block px-3 py-3 text-base font-medium text-zinc-700 hover:bg-zinc-50 rounded-md">
                    {user.role === "admin" ? "Control Tower" : "Dashboard"}
                  </Link>
                  {user.role !== "admin" && null}
                  <button onClick={handleLogout} data-testid="mobile-nav-logout"
                    className="block w-full text-left px-3 py-3 text-base font-medium text-zinc-700 hover:bg-zinc-50 rounded-md">
                    Log out
                  </button>
                </>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-nav-login"
                  className="block px-3 py-3 text-base font-medium text-zinc-700 hover:bg-zinc-50 rounded-md">
                  Log in
                </Link>
              )}
              {(!user || user.role !== "candidate") && (
                <Link to="/post-a-job" onClick={() => setOpen(false)} data-testid="mobile-nav-post-job"
                  className="block text-center bg-zinc-950 text-white font-semibold px-4 py-3 rounded-md">
                  Post a job
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
